import { ChangeEvent, FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { EmptyState, PageHeader, StatCard } from '../components/UI';
import { useAuth } from '../state/AuthContext';
import { isSupabaseConfigured, supabase } from '../lib/supabase';
import { parseQuestionText, validateQuestionRecords } from '../lib/importValidator';
import type { ImportPreview, QuestionCatalogRow, RawQuestionRecord, ServerImportReport } from '../lib/importTypes';
import {
  commitServerBatch,
  createImportBatch,
  demoCatalog,
  fetchQuestionCatalog,
  publishQuestion,
  recordReview,
  retireQuestion,
  saveQuestionDraft,
  stageImportRecords,
  submitForReview,
  validateServerBatch,
} from '../lib/contentRepository';

const statusOptions = [
  'all', 'draft', 'validation_failed', 'awaiting_content_review', 'changes_requested',
  'content_approved', 'awaiting_visual_production', 'awaiting_visual_review',
  'ready_to_publish', 'published', 'retired', 'rejected',
];

function titleCase(value: string) {
  return value.replaceAll('_', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function StatusPill({ status }: { status: string }) {
  const tone = status === 'published' || status === 'ready_to_publish'
    ? 'green'
    : status.includes('review') || status === 'content_approved'
      ? 'purple'
      : status === 'rejected' || status === 'validation_failed'
        ? 'red'
        : status === 'changes_requested'
          ? 'amber'
          : '';
  return <span className={`pill ${tone}`}>{titleCase(status)}</span>;
}

function formatDate(value: string) {
  try { return new Intl.DateTimeFormat('en', { dateStyle: 'medium' }).format(new Date(value)); }
  catch { return value; }
}

export function QuestionBankPage() {
  const { user } = useAuth();
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('all');
  const [rows, setRows] = useState<QuestionCatalogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetchQuestionCatalog(query, status)
      .then((data) => { if (active) setRows(data); })
      .catch((error: Error) => { if (active) setMessage(error.message); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [query, status]);

  async function runAction(row: QuestionCatalogRow, action: 'review' | 'publish' | 'retire') {
    setMessage('');
    try {
      if (!isSupabaseConfigured || user?.demo) {
        setRows((current) => current.map((item) => item.id === row.id
          ? { ...item, status: action === 'review' ? 'awaiting_content_review' : action === 'publish' ? 'published' : 'retired' }
          : item));
        setMessage(`Demo action completed: ${action}.`);
        return;
      }
      if (action === 'review') await submitForReview(row.id);
      if (action === 'publish') await publishQuestion(row.id);
      if (action === 'retire') await retireQuestion(row.id, 'Retired from the Admin Question Bank.');
      setRows(await fetchQuestionCatalog(query, status));
      setMessage(`${row.external_id} updated successfully.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Action failed.');
    }
  }

  const totals = useMemo(() => ({
    shown: rows.length,
    drafts: rows.filter((row) => row.status === 'draft').length,
    review: rows.filter((row) => row.status.includes('review') || row.status === 'content_approved').length,
    published: rows.filter((row) => row.status === 'published').length,
  }), [rows]);

  return (
    <section className="portal-page">
      <PageHeader
        title="Question Bank"
        description="Search, review and control versioned question records. Correct answers stay in the private schema."
        actions={<Link className="button" to="/admin/questions/new">New draft</Link>}
      />
      <div className="stats-grid compact-stats">
        <StatCard label="Records shown" value={totals.shown} note="Filtered result" />
        <StatCard label="Drafts" value={totals.drafts} note="Not visible to students" tone="amber" />
        <StatCard label="In review" value={totals.review} note="Content or specialist QA" tone="purple" />
        <StatCard label="Published" value={totals.published} note="Available to approved quizzes" tone="green" />
      </div>
      <div className="toolbar multi-toolbar">
        <input aria-label="Search questions" placeholder="Search ID or prompt" value={query} onChange={(event) => setQuery(event.target.value)} />
        <select aria-label="Filter by status" value={status} onChange={(event) => setStatus(event.target.value)}>
          {statusOptions.map((item) => <option key={item} value={item}>{titleCase(item)}</option>)}
        </select>
        <Link className="button secondary" to="/admin/import">Import batch</Link>
      </div>
      {message && <p className={message.toLowerCase().includes('fail') || message.toLowerCase().includes('error') ? 'form-message error' : 'form-message'}>{message}</p>}
      {loading ? <div className="panel">Loading question catalogueâ€¦</div> : rows.length === 0 ? (
        <EmptyState title="No matching questions" text="Change the search filters or import a validated draft batch." />
      ) : (
        <div className="table-wrap"><table><thead><tr>
          <th>ID</th><th>Level</th><th>Subject</th><th>Type</th><th>Difficulty</th><th>Status</th><th>Assets</th><th>Updated</th><th>Actions</th>
        </tr></thead><tbody>{rows.map((row) => <tr key={row.id}>
          <td><strong>{row.external_id}</strong><small className="cell-subtext">{row.lot_id || 'Manual draft'}</small></td>
          <td>{row.level_name}</td><td>{row.subject_name}</td><td>{titleCase(row.question_type)}</td><td>{row.difficulty}</td>
          <td><StatusPill status={row.status} /></td><td>{row.asset_count}</td><td>{formatDate(row.updated_at)}</td>
          <td><div className="row-actions">
            {['draft', 'changes_requested'].includes(row.status) && <button type="button" onClick={() => void runAction(row, 'review')}>Submit</button>}
            {row.status === 'ready_to_publish' && user?.role === 'super_admin' && <button type="button" onClick={() => void runAction(row, 'publish')}>Publish</button>}
            {row.status === 'published' && user?.role === 'super_admin' && <button type="button" onClick={() => void runAction(row, 'retire')}>Retire</button>}
          </div></td>
        </tr>)}</tbody></table></div>
      )}
      <p className="table-note">Demo mode shows sample records. Connected mode reads the security-invoker catalogue view under Supabase RLS.</p>
    </section>
  );
}

function Breakdown({ title, values }: { title: string; values: Record<string, number> }) {
  return <article className="breakdown-card"><h3>{title}</h3><div>{Object.entries(values).sort((a, b) => b[1] - a[1]).map(([key, value]) => <span key={key}><b>{key}</b><em>{value}</em></span>)}</div></article>;
}

export function ImportQuestionsPage() {
  const { user } = useAuth();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [serverReport, setServerReport] = useState<ServerImportReport | null>(null);
  const [batchId, setBatchId] = useState('');
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  async function validateFile(event: ChangeEvent<HTMLInputElement>) {
    const selected = event.target.files?.[0] || null;
    setFile(selected); setPreview(null); setServerReport(null); setBatchId(''); setMessage(''); setProgress(0);
    if (!selected) return;
    try {
      const records = parseQuestionText(await selected.text(), selected.name);
      setPreview(validateQuestionRecords(records));
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Unable to parse this file.');
    }
  }

  async function uploadDryRun() {
    if (!file || !preview) return;
    if (!isSupabaseConfigured || user?.demo) {
      setMessage('Client dry run passed. Connect staging Supabase to run the protected server dry run.');
      return;
    }
    setBusy(true); setMessage(''); setProgress(0);
    try {
      const format = file.name.split('.').pop()?.toLowerCase() || 'jsonl';
      const id = await createImportBatch(file.name, format, preview.totalRows);
      setBatchId(id);
      await stageImportRecords(id, preview.records, 100, setProgress);
      const report = await validateServerBatch(id);
      setServerReport(report);
      setMessage('Protected server dry run completed. Review all errors before committing drafts.');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Server dry run failed.');
    } finally { setBusy(false); }
  }

  async function commitDrafts() {
    if (!batchId || !serverReport || serverReport.valid_rows === 0) return;
    if (serverReport.invalid_rows > 0 && !window.confirm('Invalid rows will remain uncommitted. Commit only valid records as drafts?')) return;
    setBusy(true); setMessage('');
    try {
      const result = await commitServerBatch(batchId);
      setMessage(`${result.committed_rows} records committed as unpublished drafts; ${result.skipped_rows} skipped.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Draft commit failed.');
    } finally { setBusy(false); }
  }

  const visibleIssues = preview?.issues.slice(0, 150) || [];

  return (
    <section className="portal-page">
      <PageHeader title="Controlled Question Import" description="Parse locally, run a protected server dry run, then commit only valid records as unpublished drafts." />
      <div className="workflow-steps">
        <span className={file ? 'done' : 'active'}>1. Choose file</span>
        <span className={preview ? 'done' : ''}>2. Client validation</span>
        <span className={serverReport ? 'done' : ''}>3. Server dry run</span>
        <span>4. Commit drafts</span>
      </div>
      <label className="drop-zone"><input type="file" accept=".json,.jsonl,.csv" onChange={(event) => void validateFile(event)} />
        <strong>{file ? file.name : 'Choose JSONL, JSON or CSV'}</strong>
        <span>Recommended production format: JSONL. Answer keys are transferred only to protected server functions.</span>
      </label>
      {message && <p className={message.toLowerCase().includes('fail') || message.toLowerCase().includes('error') ? 'form-message error' : 'form-message'}>{message}</p>}
      {preview && <>
        <div className="stats-grid import-stats">
          <StatCard label="Rows" value={preview.totalRows} note="Parsed locally" />
          <StatCard label="Valid" value={preview.validRows} note="May enter server dry run" tone="green" />
          <StatCard label="Warnings" value={preview.warningRows} note="Review before publishing" tone="amber" />
          <StatCard label="Invalid" value={preview.invalidRows} note="Will never be committed" tone="purple" />
        </div>
        <div className="breakdown-grid">
          <Breakdown title="By level" values={preview.countsByLevel} />
          <Breakdown title="By subject" values={preview.countsBySubject} />
          <Breakdown title="By type" values={preview.countsByType} />
        </div>
        <div className="panel action-panel">
          <div><h2>Dry-run controls</h2><p>Client validation cannot detect records already stored in Supabase. The server dry run checks database duplicates and reference data.</p></div>
          <button className="button" type="button" disabled={busy || preview.totalRows === 0} onClick={() => void uploadDryRun()}>{busy ? 'Processingâ€¦' : 'Run protected server dry run'}</button>
          {busy && preview.totalRows > 0 && <progress max={preview.totalRows} value={progress}>{progress}</progress>}
        </div>
        {visibleIssues.length > 0 && <div className="panel"><div className="panel-heading"><h2>Client issues</h2><span className="pill">Showing {visibleIssues.length} of {preview.issues.length}</span></div>
          <div className="table-wrap"><table><thead><tr><th>Row</th><th>ID</th><th>Severity</th><th>Code</th><th>Message</th></tr></thead><tbody>
            {visibleIssues.map((issue, index) => <tr key={`${issue.row}-${issue.code}-${index}`}><td>{issue.row}</td><td>{issue.externalId}</td><td><span className={`pill ${issue.severity === 'error' ? 'red' : 'amber'}`}>{issue.severity}</span></td><td><code>{issue.code}</code></td><td>{issue.message}</td></tr>)}
          </tbody></table></div>
        </div>}
      </>}
      {serverReport && <div className="panel server-report"><h2>Server validation report</h2>
        <div className="report-numbers"><span>Total <b>{serverReport.total_rows}</b></span><span>Valid <b>{serverReport.valid_rows}</b></span><span>Warnings <b>{serverReport.warning_rows}</b></span><span>Invalid <b>{serverReport.invalid_rows}</b></span></div>
        <p>Batch ID: <code>{batchId}</code></p>
        <button className="button" type="button" disabled={busy || serverReport.valid_rows === 0} onClick={() => void commitDrafts()}>Commit valid records as drafts</button>
      </div>}
      <p className="notice"><strong>Safety rule:</strong> Import never publishes content. Every record starts as a draft and must complete all required reviews.</p>
    </section>
  );
}

export function ReviewQueuePage() {
  const { user } = useAuth();
  const [rows, setRows] = useState<QuestionCatalogRow[]>([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    if (!isSupabaseConfigured || user?.demo) {
      setRows(demoCatalog.filter((row) => ['awaiting_content_review', 'content_approved', 'awaiting_visual_review'].includes(row.status)));
      setLoading(false);
      return () => { active = false; };
    }
    supabase.from('admin_review_queue').select('*').order('updated_at', { ascending: true }).limit(250)
      .then(({ data, error }) => {
        if (!active) return;
        if (error) setMessage(error.message);
        else setRows((data || []) as QuestionCatalogRow[]);
        setLoading(false);
      });
    return () => { active = false; };
  }, [user?.demo]);

  async function decide(row: QuestionCatalogRow, decision: 'approved' | 'changes_requested' | 'rejected') {
    try {
      if (!isSupabaseConfigured || user?.demo) {
        setRows((current) => current.filter((item) => item.id !== row.id));
        setMessage(`Demo review recorded: ${decision}.`);
        return;
      }
      const reviewType = row.status === 'awaiting_visual_review' ? 'visual' : row.subject_name === 'Urdu' ? 'urdu' : row.subject_name === 'Islamiat' ? 'islamiyat' : 'content';
      await recordReview(row.current_version_id, reviewType, decision);
      setRows((current) => current.filter((item) => item.id !== row.id));
      setMessage(`${row.external_id}: ${decision.replaceAll('_', ' ')}.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Review failed.');
    }
  }

  return <section className="portal-page">
    <PageHeader title="Review Queue" description="Content, Urdu, Islamiat and visual review decisions are recorded independently and retained as history." />
    {message && <p className={message.toLowerCase().includes('fail') || message.toLowerCase().includes('not review') ? 'form-message error' : 'form-message'}>{message}</p>}
    {loading ? <div className="panel">Loading review queueâ€¦</div> : rows.length === 0 ? <EmptyState title="No reviews assigned" text="Validated questions enter this queue after a Content Admin submits them." /> :
      <div className="review-grid">{rows.map((row) => <article className="review-card" key={row.id}>
        <div className="review-card-top"><div><span>{row.external_id}</span><h2>{row.prompt}</h2></div><StatusPill status={row.status} /></div>
        <dl><div><dt>Level</dt><dd>{row.level_name}</dd></div><div><dt>Subject</dt><dd>{row.subject_name}</dd></div><div><dt>Type</dt><dd>{titleCase(row.question_type)}</dd></div><div><dt>Assets</dt><dd>{row.asset_count}</dd></div></dl>
        <p className="review-warning">Reviewers cannot review question versions they created. The database enforces this separation.</p>
        <div className="review-actions"><button className="approve" type="button" onClick={() => void decide(row, 'approved')}>Approve</button><button className="changes" type="button" onClick={() => void decide(row, 'changes_requested')}>Request changes</button><button className="reject" type="button" onClick={() => void decide(row, 'rejected')}>Reject</button></div>
      </article>)}</div>}
  </section>;
}

export function QuestionEditorPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    externalId: '', levelCode: 'C1', subjectCode: 'ENG', topic: '', questionType: 'single_choice', language: 'en', difficulty: 2,
    prompt: '', optionsText: 'Option A\nOption B\nOption C\nOption D', answerText: 'Option A', explanation: '', tagsText: '',
  });
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  function update(name: string, value: string | number) { setForm((current) => ({ ...current, [name]: value })); }

  async function submit(event: FormEvent) {
    event.preventDefault(); setBusy(true); setMessage('');
    try {
      const options = form.optionsText.split(/\r?\n/).map((text, index) => ({ id: `O${index + 1}`, text: text.trim() })).filter((item) => item.text);
      const answer = form.questionType === 'true_false'
        ? { value: form.answerText.trim().toLowerCase() === 'true' }
        : { value: form.answerText.trim() };
      if (!isSupabaseConfigured || user?.demo) {
        setMessage('Demo draft validated. Connect Supabase to save it to the private-answer architecture.');
        return;
      }
      const id = await saveQuestionDraft({
        externalId: form.externalId, levelCode: form.levelCode, subjectCode: form.subjectCode, topic: form.topic,
        questionType: form.questionType, language: form.language, difficulty: Number(form.difficulty), prompt: form.prompt,
        options, answerKey: answer, explanation: form.explanation, tags: form.tagsText.split(',').map((tag) => tag.trim()).filter(Boolean),
      });
      setMessage(`Draft saved with internal ID ${id}.`);
      window.setTimeout(() => navigate('/admin/questions'), 700);
    } catch (error) { setMessage(error instanceof Error ? error.message : 'Unable to save draft.'); }
    finally { setBusy(false); }
  }

  return <section className="portal-page">
    <PageHeader title="New Question Draft" description="Create a versioned draft. The correct answer is sent to a protected RPC and stored only in the private schema." actions={<Link className="button secondary" to="/admin/questions">Back to bank</Link>} />
    <form className="editor-grid" onSubmit={(event) => void submit(event)}>
      <div className="panel editor-main"><label>Question prompt<textarea required rows={5} value={form.prompt} onChange={(event) => update('prompt', event.target.value)} /></label>
        <label>Answer options, one per line<textarea rows={6} value={form.optionsText} onChange={(event) => update('optionsText', event.target.value)} /></label>
        <label>Correct answer<textarea required rows={2} value={form.answerText} onChange={(event) => update('answerText', event.target.value)} /></label>
        <label>Child-friendly explanation<textarea rows={4} value={form.explanation} onChange={(event) => update('explanation', event.target.value)} /></label>
      </div>
      <aside className="panel editor-settings"><label>External ID<input placeholder="Optional; generated if empty" value={form.externalId} onChange={(event) => update('externalId', event.target.value)} /></label>
        <label>Level<select value={form.levelCode} onChange={(event) => update('levelCode', event.target.value)}>{['PG','NUR','KG','C1','C2','C3','C4','C5'].map((value) => <option key={value}>{value}</option>)}</select></label>
        <label>Subject<select value={form.subjectCode} onChange={(event) => update('subjectCode', event.target.value)}>{['ENG','URD','MATH','SCI','SST','ISL'].map((value) => <option key={value}>{value}</option>)}</select></label>
        <label>Topic<input value={form.topic} onChange={(event) => update('topic', event.target.value)} /></label>
        <label>Question type<select value={form.questionType} onChange={(event) => update('questionType', event.target.value)}>{['single_choice','picture_choice','picture_count','true_false','matching','sequence','fill_blank','multi_select','short_answer'].map((value) => <option key={value}>{titleCase(value)}</option>)}</select></label>
        <label>Language<select value={form.language} onChange={(event) => update('language', event.target.value)}><option value="en">English</option><option value="ur">Urdu</option></select></label>
        <label>Difficulty<input type="number" min="1" max="5" value={form.difficulty} onChange={(event) => update('difficulty', Number(event.target.value))} /></label>
        <label>Tags<input placeholder="phonics, class_1" value={form.tagsText} onChange={(event) => update('tagsText', event.target.value)} /></label>
        <button className="button" disabled={busy} type="submit">{busy ? 'Savingâ€¦' : 'Save unpublished draft'}</button>
        {message && <p className={message.toLowerCase().includes('unable') || message.toLowerCase().includes('error') ? 'form-message error' : 'form-message'}>{message}</p>}
      </aside>
    </form>
  </section>;
}

export function AssetsPage() {
  return <section className="portal-page"><PageHeader title="3D Asset Manager" description="Manage prompt approval, rendering, optimized WebP files, alt text and visual QA." /><div className="stats-grid"><article className="stat-card tone-purple"><span>Asset specifications</span><strong>22,960</strong><small>Available in the final content package</small></article><article className="stat-card tone-amber"><span>Rendered files</span><strong>0</strong><small>Rendering has not started</small></article><article className="stat-card tone-blue"><span>Import behavior</span><strong>Auto-link</strong><small>Asset IDs are created as prompt-ready records</small></article><article className="stat-card tone-green"><span>Publication rule</span><strong>QA first</strong><small>Visual questions require visual approval</small></article></div><EmptyState title="Connect Cloudflare R2" text="After R2 is configured, rendered assets and visual-review controls will appear here." /></section>;
}

export function GenericAdminPage({ title }: { title: string }) {
  return <section className="portal-page"><PageHeader title={title} description="This protected route is ready for its scheduled work package." /><EmptyState title={`${title} is scaffolded`} text="Database tables and security policies are included; functional CRUD will be added after staging validation." /></section>;
}


